/**
 * Client API unique de l'application. Interchangeable au niveau réseau :
 *  - simulation : MSW intercepte /api/v1/* (aucun écran n'importe les fixtures) ;
 *  - réel : même code, servi par NestJS derrière Caddy (même origine, cookies HttpOnly).
 * Gère : jeton CSRF (X-CSRF-Token), Idempotency-Key, If-Match, erreurs RFC 9457, step-up MFA, session expirée.
 */
import createClient from 'openapi-fetch';
import type { paths, Probleme, CodeErreur } from '@myron/contracts';

export const BASE_API = (import.meta.env.VITE_API_BASE as string | undefined) ?? '/api/v1';

/** Client typé généré depuis le contrat OpenAPI (utilisable directement si souhaité). */
export const api = createClient<paths>({ baseUrl: BASE_API, credentials: 'include' });

export class ApiErreur extends Error {
  constructor(public status: number, public code: CodeErreur | 'RESEAU', message: string, public probleme?: Partial<Probleme> & Record<string, unknown>) {
    super(message);
  }
  /** Erreurs de champ (VALIDATION) indexées par nom de champ. */
  get champs(): Record<string, string> {
    const r: Record<string, string> = {};
    for (const e of (this.probleme?.erreurs as { champ?: string; message?: string }[] | undefined) ?? []) if (e.champ) r[e.champ] = e.message ?? 'Invalide';
    return r;
  }
}

let jetonCsrf: string | null = null;
export function definirJetonCsrf(j: string | null | undefined) {
  jetonCsrf = j ?? null;
}
async function assurerCsrf() {
  if (jetonCsrf) return jetonCsrf;
  const r = await fetch(`${BASE_API}/auth/csrf`, { credentials: 'include' });
  if (r.ok) jetonCsrf = ((await r.json()) as { jetonCsrf: string }).jetonCsrf;
  return jetonCsrf;
}

/** Gestionnaires globaux branchés par l'application (modale de ré-authentification, redirection de session expirée). */
type Reauth = () => Promise<boolean>;
let gestionnaireReauth: Reauth | null = null;
let gestionnaireSessionExpiree: (() => void) | null = null;
export function brancherReauth(fn: Reauth | null) { gestionnaireReauth = fn; }
export function brancherSessionExpiree(fn: (() => void) | null) { gestionnaireSessionExpiree = fn; }

export const nouvelleCleIdempotence = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}-cle`);

export interface OptionsAppel {
  corps?: unknown;
  requete?: Record<string, string | number | boolean | undefined | null | string[]>;
  /** Clé d'idempotence (générez-la une fois par intention utilisateur, pas par tentative). */
  idempotence?: string;
  /** Version courante → If-Match. */
  version?: number;
  /** Corps brut (FormData, texte). */
  brut?: BodyInit;
  entetes?: Record<string, string>;
  /** Ne pas déclencher le step-up automatique. */
  sansReauth?: boolean;
  /** Réponse non JSON (texte, CSV). */
  texte?: boolean;
}

function construireUrl(chemin: string, requete?: OptionsAppel['requete']) {
  const url = new URL(`${BASE_API}${chemin}`, window.location.origin);
  for (const [k, v] of Object.entries(requete ?? {})) {
    if (v === undefined || v === null || v === '') continue;
    url.searchParams.set(k, Array.isArray(v) ? v.join(',') : String(v));
  }
  return url.pathname + url.search;
}

/** Appel HTTP vers l'API (contrat). Lève ApiErreur. */
export async function appel<T = unknown>(methode: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE', chemin: string, o: OptionsAppel = {}): Promise<T> {
  const entetes: Record<string, string> = { Accept: 'application/json, application/problem+json', ...(o.entetes ?? {}) };
  if (methode !== 'GET') {
    const j = await assurerCsrf();
    if (j) entetes['X-CSRF-Token'] = j;
  }
  if (o.idempotence) entetes['Idempotency-Key'] = o.idempotence;
  if (o.version !== undefined) entetes['If-Match'] = `"${o.version}"`;
  let body: BodyInit | undefined = o.brut;
  if (o.corps !== undefined) {
    entetes['Content-Type'] = 'application/json';
    body = JSON.stringify(o.corps);
  }
  let r: Response;
  try {
    r = await fetch(construireUrl(chemin, o.requete), { method: methode, headers: entetes, body, credentials: 'include' });
  } catch {
    throw new ApiErreur(0, 'RESEAU', 'Connexion impossible. Vérifiez votre réseau et réessayez.');
  }
  if (r.status === 204) return undefined as T;
  const type = r.headers.get('content-type') ?? '';
  if (!r.ok) {
    const p = type.includes('json') ? ((await r.json().catch(() => ({}))) as Partial<Probleme> & Record<string, unknown>) : {};
    const e = new ApiErreur(r.status, (p.code as CodeErreur) ?? 'ERREUR_INTERNE', (p.title as string) ?? `Erreur ${r.status}`, p);
    if (e.code === 'MFA_RECENTE_REQUISE' && !o.sansReauth && gestionnaireReauth) {
      if (await gestionnaireReauth()) return appel<T>(methode, chemin, { ...o, sansReauth: true });
    }
    if (e.code === 'CSRF_INVALIDE') jetonCsrf = null;
    if (e.status === 401 && e.code === 'NON_AUTHENTIFIE' && p.sessionExpiree && gestionnaireSessionExpiree) gestionnaireSessionExpiree();
    throw e;
  }
  if (o.texte || !type.includes('json')) return (await r.text()) as T;
  return (await r.json()) as T;
}

export const get = <T>(chemin: string, requete?: OptionsAppel['requete']) => appel<T>('GET', chemin, { requete });
export const post = <T>(chemin: string, corps?: unknown, o: Omit<OptionsAppel, 'corps'> = {}) => appel<T>('POST', chemin, { ...o, corps });
export const put = <T>(chemin: string, corps?: unknown, o: Omit<OptionsAppel, 'corps'> = {}) => appel<T>('PUT', chemin, { ...o, corps });
export const patch = <T>(chemin: string, corps?: unknown, o: Omit<OptionsAppel, 'corps'> = {}) => appel<T>('PATCH', chemin, { ...o, corps });
export const del = <T>(chemin: string, o: OptionsAppel = {}) => appel<T>('DELETE', chemin, o);

/** Message lisible pour une erreur quelconque. */
export function messageErreur(e: unknown): string {
  if (e instanceof ApiErreur) return e.message;
  if (e instanceof Error) return e.message;
  return 'Une erreur est survenue.';
}
